/**
 * dotKit — dot-grid geometry in PHYSICAL units: the access & counting
 * foundation (CATALOG F1 ten-frame / F5 braille strip).
 *
 * Serves: ten-frame (now) · braille caption strip (wave 3 — the geometry is
 * this kit; the Grade-1 translation table is separate pure data) · tally
 * dots · dice/domino patterns.
 *
 * THE PHYSICAL-UNITS LAW: the workspace is 72 units per inch (the editor's
 * canvas is "24×36 inches at 72 DPI" and print export rasterizes at
 * 300 dpi by multiplying 300/72 — so 1 workspace unit prints at exactly
 * 1/72 in). Dot spacing that must be TACTILE-correct (braille) or
 * manipulative-correct (ten-frame cells big enough for little fingers) is
 * therefore specified in INCHES here and converted once, not eyeballed in
 * px. Callers that scale the resulting group void the physical guarantee —
 * signage-grade recipes insert at scale 1.
 *
 * Pure layout math only — see kits/specs.ts. Local origin (0,0 = top-left).
 */
import type { CircleSpec, KitBlock, LineSpec, PrimitiveSpec, RectSpec } from './specs';

/** The workspace/export law: 72 workspace units = 1 inch (300dpi = ×300/72). */
export const UNITS_PER_INCH = 72;

/** Inches → workspace units. */
export function inchesToUnits(inches: number): number {
  return inches * UNITS_PER_INCH;
}

/**
 * ADA/signage braille cell geometry (2010 ADA Standards §703.3, reference
 * values for wave 3 — recorded here so the braille recipe inherits measured
 * numbers, not guesses): base dot diameter 0.059–0.063 in; same-cell dot
 * pitch 0.090–0.100 in; corresponding-dot pitch between adjacent cells
 * 0.241–0.300 in; baseline-to-baseline 0.395–0.400 in. The mid/compliant
 * values below are what the geometry should default to.
 */
export const ADA_BRAILLE = {
  dotDiameterIn: 0.06,
  dotPitchIn: 0.095,
  cellPitchIn: 0.26,
  linePitchIn: 0.4,
} as const;

// ---------------------------------------------------------------------------
// Dot grid
// ---------------------------------------------------------------------------

export interface DotGridOptions {
  rows: number;
  cols: number;
  /** Center-to-center pitch, in INCHES (the physical-units law). */
  pitchXIn: number;
  pitchYIn: number;
  /** Dot diameter in INCHES. */
  dotDiameterIn: number;
  /**
   * Which dots are FILLED: a count (reading order — left→right, top→bottom)
   * or an explicit boolean matrix [row][col].
   */
  filled: number | boolean[][];
  fillColor: string;
  /** Empty dots render as outlines; omit emptyStroke to skip them entirely. */
  emptyStroke?: string;
  emptyStrokeWidth?: number;
}

export interface DotGridBlock extends KitBlock {
  /** Dot centers in workspace units, row-major — the exact-spacing law. */
  centers: { x: number; y: number }[];
  filledCount: number;
}

/**
 * Rectangular dot array with EXACT physical spacing: center (r,c) sits at
 * (c * pitchX, r * pitchY) workspace units from the first dot's center —
 * pitch converted from inches once, then pure multiplication (no per-dot
 * rounding drift; print positions are exact multiples).
 *
 * The block origin is the top-left dot's bounding corner (center - radius),
 * so bounds hug the ink.
 */
export function dotGrid(opts: DotGridOptions): DotGridBlock {
  const rows = Math.max(1, Math.round(opts.rows));
  const cols = Math.max(1, Math.round(opts.cols));
  const pitchX = inchesToUnits(opts.pitchXIn);
  const pitchY = inchesToUnits(opts.pitchYIn);
  const r = inchesToUnits(opts.dotDiameterIn) / 2;

  const isFilled = (row: number, col: number): boolean => {
    if (Array.isArray(opts.filled)) return !!opts.filled[row]?.[col];
    return row * cols + col < opts.filled;
  };

  const specs: PrimitiveSpec[] = [];
  const centers: { x: number; y: number }[] = [];
  let filledCount = 0;
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const cx = r + col * pitchX;
      const cy = r + row * pitchY;
      centers.push({ x: cx, y: cy });
      const filled = isFilled(row, col);
      if (filled) filledCount += 1;
      if (!filled && !opts.emptyStroke) continue;
      const dot: CircleSpec = filled
        ? { kind: 'circle', cx, cy, r, fill: opts.fillColor }
        : {
            kind: 'circle',
            cx,
            cy,
            r,
            fill: '',
            stroke: opts.emptyStroke,
            strokeWidth: opts.emptyStrokeWidth ?? Math.max(1, r * 0.18),
          };
      specs.push(dot);
    }
  }
  return {
    specs,
    width: 2 * r + (cols - 1) * pitchX,
    height: 2 * r + (rows - 1) * pitchY,
    centers,
    filledCount,
  };
}

// ---------------------------------------------------------------------------
// Ten-frame
// ---------------------------------------------------------------------------

export interface TenFrameOptions {
  /** Counters shown, 0..10, filled in reading order (the K–2 convention). */
  filled: number;
  /** Cell size in INCHES. Default 0.75 — finger-sized on a printed poster. */
  cellIn?: number;
  /** Dot diameter as a fraction of the cell. Default 0.62. */
  dotRatio?: number;
  frameColor: string;
  dotColor: string;
  frameWeight?: number;
  gridWeight?: number;
}

export interface TenFrameBlock extends KitBlock {
  filled: number;
  cellPx: number;
}

/**
 * The K–2 ten-frame: a 2×5 cell frame with `filled` counters placed in
 * READING ORDER (top row left→right first — the subitizing convention every
 * curriculum teaches). Cells are physically sized (inches); dots center in
 * their cells by construction. Frame + interior grid lines + dots only —
 * plain primitives.
 */
export function tenFrame(opts: TenFrameOptions): TenFrameBlock {
  const filled = Math.max(0, Math.min(10, Math.round(opts.filled)));
  const cell = inchesToUnits(opts.cellIn ?? 0.75);
  const cols = 5;
  const rows = 2;
  const w = cols * cell;
  const h = rows * cell;
  const frameWeight = opts.frameWeight ?? Math.max(2, cell * 0.06);
  const gridWeight = opts.gridWeight ?? Math.max(1.5, cell * 0.035);
  const dotR = (cell * (opts.dotRatio ?? 0.62)) / 2;

  const specs: PrimitiveSpec[] = [];
  const frame: RectSpec = {
    kind: 'rect',
    x: 0,
    y: 0,
    width: w,
    height: h,
    fill: '',
    stroke: opts.frameColor,
    strokeWidth: frameWeight,
  };
  specs.push(frame);
  for (let c = 1; c < cols; c++) {
    const v: LineSpec = {
      kind: 'line',
      x1: c * cell,
      y1: 0,
      x2: c * cell,
      y2: h,
      stroke: opts.frameColor,
      strokeWidth: gridWeight,
      lineCap: 'butt',
    };
    specs.push(v);
  }
  const mid: LineSpec = {
    kind: 'line',
    x1: 0,
    y1: cell,
    x2: w,
    y2: cell,
    stroke: opts.frameColor,
    strokeWidth: gridWeight,
    lineCap: 'butt',
  };
  specs.push(mid);

  for (let i = 0; i < filled; i++) {
    const row = Math.floor(i / cols);
    const col = i % cols;
    const dot: CircleSpec = {
      kind: 'circle',
      cx: col * cell + cell / 2,
      cy: row * cell + cell / 2,
      r: dotR,
      fill: opts.dotColor,
    };
    specs.push(dot);
  }

  return { specs, width: w, height: h, filled, cellPx: cell };
}
